import { useEffect, useRef } from 'react'
import mqtt, { type MqttClient } from 'mqtt'
import { z } from 'zod'
import { useUiStore } from '../stores/ui'

const envelopeSchema = z.looseObject({
  schema_version: z.number().optional(),
  message_id: z.string().optional(),
  event_id: z.string().optional(),
  event_version: z.number().optional(),
  expires_at: z.number().optional(),
})

export type RealtimeEnvelope = z.infer<typeof envelopeSchema>

const seen = new Set<string>()
let client: MqttClient | null = null

const setRealtimeState = (state: 'connected' | 'connecting' | 'degraded' | 'offline') => {
  useUiStore.getState().setRealtimeState(state)
}

/**
 * MQTT 实时订阅。onMessage 存入 ref，避免父组件重渲染导致重连。
 * 凭证：登录用户经 /auth/mqtt-credentials 动态下发（EMQX 匿名已关闭）。
 * 订阅主题对齐 backend/internal/emqx/publisher.go 的发布约定。
 */
export function useRealtime(onMessage: (topic: string, data: RealtimeEnvelope) => void) {
  const handlerRef = useRef(onMessage)
  useEffect(() => {
    handlerRef.current = onMessage
  })

  useEffect(() => {
    let disposed = false
    const url = import.meta.env.VITE_REALTIME_URL
    if (!url) {
      setRealtimeState('offline')
      return
    }
    setRealtimeState('connecting')
    const clientId = `mirror-web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

    const connect = async (username?: string, password?: string) => {
      client = mqtt.connect(url, {
        clientId,
        reconnectPeriod: 3000,
        connectTimeout: 8000,
        clean: true,
        ...(username ? { username, password: password ?? '' } : {}),
      })

      client.on('connect', () => {
        setRealtimeState('connected')
        void client?.subscribe([
          'alert/broadcast',
          'alert/+/+',
          'alert/+/+/+',
          'earthquake/+/+',
          'event/+/update',
          // 后端发布两级主题 trigger/{region4}/{geohash8}（emqx/publisher.go），必须两级通配
          'trigger/+/+',
          'device/+/+',
        ])
      })
      client.on('reconnect', () => setRealtimeState('connecting'))
      client.on('offline', () => setRealtimeState('degraded'))
      client.on('close', () => setRealtimeState('offline'))
      client.on('error', () => setRealtimeState('degraded'))
      client.on('message', (topic, payload) => {
        let parsed: RealtimeEnvelope
        try {
          const result = envelopeSchema.safeParse(JSON.parse(payload.toString()))
          if (!result.success) return
          parsed = result.data
        } catch {
          return
        }
        const id = parsed.message_id
        if (id && seen.has(id)) return
        if (id) {
          seen.add(id)
          if (seen.size > 500) {
            const oldest = seen.values().next().value
            if (oldest) seen.delete(oldest)
          }
        }
        if (parsed.expires_at && parsed.expires_at < Date.now()) return
        handlerRef.current(topic, parsed)
      })
    }

    // 先取登录用户的 MQTT 凭证再连接；未登录或接口失败时匿名尝试（开发环境）
    void (async () => {
      let username: string | undefined
      let password: string | undefined
      try {
        const { fetchMqttCredentials } = await import('../api/auth')
        const creds = await fetchMqttCredentials()
        username = creds.username
        password = creds.password
      } catch {
        // 未登录：匿名尝试（开发 broker 允许；生产 EMQX 会拒绝，显示离线属预期）
      }
      if (disposed) return
      await connect(username, password)
    })()

    return () => {
      disposed = true
      client?.end(true)
      client = null
    }
  }, [])
}
